/**
 * LeadPipelineStepper — horizontal 9-step pipeline + "Lead frío" exit.
 *
 * Replaces the long pill row on LeadDetailPage / LeadSidePanel. The
 * stepper is display-only on top of the existing PATCH /leads/{id}
 * endpoint — no schema change, no new activity event.
 *
 * Click semantics:
 *   - next-step forward → direct PATCH.
 *   - non-adjacent forward jump → confirm.
 *   - any backward move → confirm.
 *   - closed_won / lost (cold) → confirm.
 *   - clicking the current step → noop.
 *
 * Props:
 *   status      — backend status string (e.g. "contacted", "dossier", "lost")
 *   onChange    — async (nextStatus) => void; should PATCH /leads/{id}.
 *   compact     — boolean, optional; renders the small variant for SidePanel.
 *   disabled    — boolean, optional; disables all interaction.
 */
import React from "react";
import {
  PRIMARY_PIPELINE, COLD_LEAD_STATUS, COLD_LEAD_LABEL,
  pipelineStep, isColdLead, visibleStep, pipelineLabel,
  needsConfirmMove, confirmMoveText, nextVisibleStatus,
} from "@/lib/leadPipeline";
import { Snowflake, Check } from "@phosphor-icons/react";

export default function LeadPipelineStepper({
  status,
  onChange,
  compact = false,
  disabled = false,
}) {
  const currentKey = visibleStep(status);
  const cold = isColdLead(status);
  const currentStep = pipelineStep(status); // null if cold

  const handle = async (nextStatus) => {
    if (disabled || nextStatus === status) return;
    if (nextStatus === visibleStep(status) && !cold) return;
    if (needsConfirmMove(status, nextStatus)) {
      if (!window.confirm(confirmMoveText(nextStatus, status))) return;
    }
    await onChange?.(nextStatus);
  };

  return (
    <div className="dv-card" data-testid="lead-pipeline-stepper">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="text-[10px] uppercase tracking-[0.25em] text-stone-500">
          {cold
            ? "Lead frío · fuera del pipeline activo"
            : `Pipeline · paso ${currentStep ?? "—"} de ${PRIMARY_PIPELINE.length}`}
        </div>
        <button
          type="button"
          onClick={() => handle(COLD_LEAD_STATUS)}
          disabled={disabled || cold}
          data-testid="lead-pipeline-cold"
          className={`text-[11px] px-2 py-1 rounded-full border inline-flex items-center gap-1 transition ${
            cold
              ? "bg-sky-50 border-sky-300 text-sky-800"
              : "bg-white border-stone-200 text-stone-600 hover:border-sky-300 hover:text-sky-700"
          } disabled:opacity-50`}
        >
          <Snowflake size={11} weight={cold ? "fill" : "regular"} />
          {COLD_LEAD_LABEL}
        </button>
      </div>

      <div
        className={`flex items-center ${compact ? "gap-0.5" : "gap-1"} overflow-x-auto`}
        role="list"
        aria-label="Pipeline del lead"
      >
        {PRIMARY_PIPELINE.map((s, idx) => {
          const isActive = !cold && s.key === currentKey;
          const isPast =
            !cold && currentStep != null && s.step < currentStep;
          const isLast = idx === PRIMARY_PIPELINE.length - 1;
          return (
            <React.Fragment key={s.key}>
              <div className="flex flex-col items-center gap-1 shrink-0">
                <button
                  type="button"
                  onClick={() => handle(s.key)}
                  disabled={disabled}
                  role="listitem"
                  aria-current={isActive ? "step" : undefined}
                  title={s.label}
                  data-testid={`lead-pipeline-step-${s.key}`}
                  data-state={isActive ? "current" : isPast ? "past" : "future"}
                  className={`shrink-0 inline-flex items-center justify-center transition rounded-full ${
                    compact ? "w-7 h-7 text-[10px]" : "w-9 h-9 text-xs"
                  } ${
                    isActive
                      ? "bg-stone-900 text-white border-2 border-stone-900 shadow-sm"
                      : isPast
                        ? "bg-stone-700 text-white border border-stone-700"
                        : "bg-white text-stone-400 border border-stone-200 hover:border-stone-400 hover:text-stone-700"
                  } ${cold ? "opacity-50" : ""} disabled:opacity-40 disabled:cursor-not-allowed`}
                >
                  {isPast ? <Check size={compact ? 11 : 13} weight="bold" /> : s.step}
                </button>
                {/* Short label under each circle — desktop only in the full
                    variant. Compact variant (sidebar) and mobile fall back to
                    the "Actual / Siguiente" line below. */}
                {!compact && (
                  <span
                    className={`hidden md:block text-[10px] leading-tight text-center max-w-[64px] truncate ${
                      isActive ? "text-stone-900 font-semibold" : "text-stone-500"
                    } ${cold ? "opacity-50" : ""}`}
                    data-testid={`lead-pipeline-step-label-${s.key}`}
                  >
                    {s.shortLabel}
                  </span>
                )}
              </div>
              {!isLast && (
                <span
                  className={`h-px flex-1 min-w-[6px] ${
                    isPast ? "bg-stone-700" : "bg-stone-200"
                  } ${cold ? "opacity-50" : ""} ${
                    !compact ? "md:self-start md:mt-[18px]" : ""
                  }`}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Current state + next step. The "Siguiente" half is hidden when the
          lead has no logical next step (cold/terminal/closed_won) so we don't
          render a misleading "—" placeholder. */}
      {(() => {
        const nextKey = cold ? null : nextVisibleStatus(status);
        const showNext = !cold && nextKey != null;
        return (
          <div className={`mt-3 ${compact ? "text-[11px]" : "text-xs"} text-stone-600 flex flex-wrap items-center gap-x-3 gap-y-1`}>
            <div className="flex items-center gap-1.5" data-testid="lead-pipeline-current">
              <span className="text-stone-400 uppercase tracking-widest text-[10px]">Actual:</span>
              <span className={`font-medium ${cold ? "text-sky-800" : "text-stone-800"}`}>
                {pipelineLabel(status)}
              </span>
            </div>
            {showNext && (
              <div className="flex items-center gap-1.5" data-testid="lead-pipeline-next">
                <span className="text-stone-400 uppercase tracking-widest text-[10px]">Siguiente:</span>
                <span className="font-medium text-stone-700">
                  {pipelineLabel(nextKey)}
                </span>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}
